import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import {
  Calendar, Clock, Search, Filter, AlertCircle,
  ChevronDown, FileText, Shield, CheckCircle,
  User, Book, Info, ChevronLeft, ChevronRight
} from 'lucide-react';
import {
  format, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  isSameDay, isWithinInterval, isSameMonth,
  addDays, addWeeks, addMonths, subDays, subWeeks, subMonths,
  startOfDay, endOfDay, parseISO
} from 'date-fns';

// Helper Components
const StatusBadge = ({ status }) => {
  const styles = {
    'in-progress': 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    reopened: 'bg-orange-100 text-orange-800'
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full font-medium ${styles[status] || 'bg-gray-100'}`}>
      {status?.replace('-', ' ').split(' ').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')}
    </span>
  );
};

const RiskLevelBadge = ({ level }) => {
  const styles = {
    none: 'bg-gray-100 text-gray-800',
    low: 'bg-blue-100 text-blue-800',
    moderate: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    severe: 'bg-red-100 text-red-800'
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full font-medium ${styles[level] || 'bg-gray-100'}`}>
      {level.charAt(0).toUpperCase() + level.slice(1)} Risk
    </span>
  );
};

const SectionTitle = ({ children, icon: Icon }) => (
  <div className="flex items-center space-x-2 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
    {Icon && <Icon className="h-5 w-5 text-gray-500 dark:text-gray-400" />}
    <h3 className="text-lg font-medium text-gray-900 dark:text-white">{children}</h3>
  </div>
);

const DataItem = ({ label, value, type = "text" }) => {
  if (!value && value !== 0) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderValue = () => {
    if (type === "date") return formatDate(value);
    if (type === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.length === 0 ? "None" : value.join(", ");
    return value;
  };

  return (
    <div className="py-2">
      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}:</span>
      <span className="ml-2 text-gray-900 dark:text-white">{renderValue()}</span>
    </div>
  );
};

// Session Content Component - Reused from SessionHistory
const SessionContent = ({ session, userRole, onContinueSession, onReopenSession, selectedSessionRecord, recordLoading }) => {
  const canViewSensitiveData = ['counselor', 'hod'].includes(userRole);
  const canViewBasicData = ['student', 'receptionist'].includes(userRole);
  const isInProgress = session.status === 'in-progress';
  const isCompleted = session.status === 'completed';
  const canContinueSession = ['counselor', 'hod'].includes(userRole) && isInProgress;
  const canReopenSession = ['hod'].includes(userRole) && isCompleted;
  // const isInitialSession = session.record?.sessionNumber === '1' || session.record?.sessionNumber === 1;
  const isInitialSession = (session.record?.sessionNumber === 1 || session.record?.sessionNumber === '1') &&
    !selectedSessionRecord?.sessionNumber;


  const handleContinueSession = () => {
    if (session.appointment?._id) {
      onContinueSession(session.appointment._id);
    } else {
      console.error("Cannot continue session: Appointment ID is missing");
    }
  };


  const getDisplayName = (user) => {
    return user?.fullName || user?.username || 'N/A';
  };

  return (
    <div className="space-y-6">
      {/* Basic Session Information - Visible to all */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
        <DataItem
          label="Date"
          value={session.record?.sessionDate}
          type="date"
        />
        <DataItem
          label="Session Number"
          value={session.record?.sessionNumber}
        />
        {session.record?.followUpDate && (
          <DataItem
            label="Follow-up Date"
            value={session.record?.followUpDate}
            type="date"
          />
        )}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-500">Status:</span>
          <StatusBadge status={session.status} />
        </div>
      </div>

      {/* Participants Section */}
      <div>
        <SectionTitle icon={User}>Participants</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Counselor</h4>
            <p className="font-medium">{getDisplayName(session.counselor)}</p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Client</h4>
            <p className="font-medium">{getDisplayName(session.client)}</p>
          </div>
        </div>
      </div>

      {/* Clinical Content - Only visible to authorized roles */}
      {canViewSensitiveData && (
        <>
          {/* Initial Session Content */}
          {isInitialSession ? (
            <>
              {/* Intake Information */}
              <div>
                <SectionTitle icon={Book}>Intake Assessment</SectionTitle>
                <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg space-y-4">
                  {session.intake?.presentingProblems && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Presenting Problems</h5>
                      {Array.isArray(session.intake.presentingProblems) ? (
                        <div className="space-y-2">
                          {session.intake.presentingProblems.map((problem, index) => (
                            <p key={index} className="text-gray-600 dark:text-gray-400">{problem}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{session.intake.presentingProblems}</p>
                      )}
                    </div>
                  )}

                  {session.intake?.diagnosis && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Initial Diagnosis</h5>
                      <p className="text-gray-600 dark:text-gray-400">{session.intake.diagnosis}</p>
                    </div>
                  )}

                  {session.intake?.goals && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Goals Discussed</h5>
                      {Array.isArray(session.intake.goals) ? (
                        <div className="space-y-2">
                          {session.intake.goals.map((goal, index) => (
                            <p key={index} className="text-gray-600 dark:text-gray-400">{goal}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{session.intake.goals}</p>
                      )}
                    </div>
                  )}

                  {session.intake?.impactOnFunctioning && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Impact on Functioning</h5>
                      <div className="space-y-2">
                        {session.intake.impactOnFunctioning.current && (
                          <div>
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Current:</span>
                            <p className="text-gray-600 dark:text-gray-400 ml-2">{session.intake.impactOnFunctioning.current}</p>
                          </div>
                        )}
                        {session.intake.impactOnFunctioning.past && (
                          <div>
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Past:</span>
                            <p className="text-gray-600 dark:text-gray-400 ml-2">{session.intake.impactOnFunctioning.past}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {session.intake?.nextSteps && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Next Steps</h5>
                      <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{session.intake.nextSteps}</p>
                    </div>
                  )}
                  {session.intake?.history && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">History</h5>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Past Counseling:</span>
                      <p className="text-gray-600 dark:text-gray-400 ml-2 mb-2">{session.intake.history.pastCounseling}</p>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Substance History:</span>
                      <p className="text-gray-600 dark:text-gray-400 ml-2 mb-2">{session.intake.history.substanceUseHistory}</p>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Family Background:</span>
                      <p className="text-gray-600 dark:text-gray-400 ml-2 mb-2">{session.intake.history.familyBackground}</p>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Medical History:</span>
                      <p className="text-gray-600 dark:text-gray-400 ml-2 mb-2">{session.intake.history.medicalHistory}</p>

                    </div>
                  )}

                  {session.intake?.interventionsUsed && session.intake.interventionsUsed.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Interventions Used</h5>
                      <div className="flex flex-wrap gap-2">
                        {session.intake.interventionsUsed.map((intervention, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                          >
                            {typeof intervention === 'string' ? intervention : intervention.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Mental Status Examination */}
              <div>
                <SectionTitle icon={Info}>Mental Status Examination</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
                  {Object.entries(session.mentalStatusExam || {}).map(([key, value]) => {
                    if (typeof value === 'object') {
                      return (
                        <div key={key} className="space-y-2">
                          <h5 className="font-medium text-gray-700 dark:text-gray-300 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </h5>
                          {Object.entries(value).map(([subKey, subValue]) => (
                            <div key={`${key}-${subKey}`} className="text-sm">
                              <span className="text-gray-500 capitalize">
                                {subKey.replace(/([A-Z])/g, ' $1').trim()}:
                              </span>
                              <span className="ml-2 text-gray-900">{subValue}</span>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return (
                      <DataItem
                        key={key}
                        label={key.replace(/([A-Z])/g, ' $1').trim()}
                        value={value}
                      />
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Follow-up Session Content - Counseling Record */}
              <div>
                {recordLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div>
                    <SectionTitle icon={FileText}>On-Going Counseling Report</SectionTitle>

                    {recordLoading ? (
                      <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    ) : selectedSessionRecord ? (
                      <div className="space-y-6">
                        {/* Initial Diagnosis */}
                        {selectedSessionRecord.initialDiagnosis && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Initial Diagnosis</h5>
                            <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
                              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedSessionRecord.initialDiagnosis}</p>
                            </div>
                          </div>
                        )}

                        {/* Presenting Issues */}
                        {selectedSessionRecord.presentingIssues && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Presenting Issues</h5>
                            <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
                              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedSessionRecord.presentingIssues}</p>
                            </div>
                          </div>
                        )}

                        {/* Goals Discussed */}
                        {selectedSessionRecord.goalsDiscussed && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Goals Discussed</h5>
                            <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
                              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedSessionRecord.goalsDiscussed}</p>
                            </div>
                          </div>
                        )}

                        {/* Progress Notes */}
                        {selectedSessionRecord.progress && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Progress Notes</h5>
                            <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
                              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedSessionRecord.progress}</p>
                            </div>
                          </div>
                        )}

                        {/* Interventions */}
                        {selectedSessionRecord.interventionsUsed?.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Interventions Used</h5>
                            <div className="flex flex-wrap gap-2">
                              {selectedSessionRecord.interventionsUsed.map((intervention, index) => (
                                <span
                                  key={index}
                                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                                >
                                  {typeof intervention === 'string' ? intervention : intervention.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Prognosis */}
                        {selectedSessionRecord.prognosis && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Prognosis</h5>
                            <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
                              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedSessionRecord.prognosis}</p>
                            </div>
                          </div>
                        )}

                        {/* Plan for Next Session */}
                        {selectedSessionRecord.planForNextSession && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Plan for Next Session</h5>
                            <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
                              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedSessionRecord.planForNextSession}</p>
                            </div>
                          </div>
                        )}

                        {/* Homework */}
                        {selectedSessionRecord.homework && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Homework/Assignments</h5>
                            <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
                              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedSessionRecord.homework}</p>
                            </div>
                          </div>
                        )}

                        {/* Follow-up Date */}
                        {selectedSessionRecord.followUpDate && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Follow-up Date</h5>
                            <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
                              <p className="text-gray-700 dark:text-gray-300">
                                {new Date(selectedSessionRecord.followUpDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
                        <p className="text-gray-500 text-center">No counseling record available for this session.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Safety Assessment - Common to both initial and follow-up */}
          <div>
            <SectionTitle icon={Shield}>Safety Assessment</SectionTitle>
            <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700 dark:text-gray-300">Risk Level</span>
                <RiskLevelBadge level={session.safety?.riskLevel || 'none'} />
              </div>
              {session.safety?.safetyContractRequired && (
                <div className="flex items-center bg-yellow-50 p-3 rounded-lg text-yellow-700">
                  <Shield className="h-5 w-5 mr-2" />
                  <span>Safety Contract Required</span>
                </div>
              )}
              {session.safety?.concerns && (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Safety Concerns</h5>
                  <p className="text-gray-600 dark:text-gray-400">{session.safety.concerns}</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Plan and Homework - Visible to client after session */}
      {(canViewBasicData || canViewSensitiveData) && !isInProgress && (
        <div>
          <SectionTitle icon={CheckCircle}>Session Summary</SectionTitle>
          <div className="space-y-4">
            {session.record?.planForNextSession && (
              <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Plan for Next Session</h5>
                <p className="text-gray-600 dark:text-gray-400">{session.record.planForNextSession}</p>
              </div>
            )}
            {session.record?.homework && (
              <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Homework/Assignments</h5>
                <p className="text-gray-600 dark:text-gray-400">{session.record.homework}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end pt-4 space-x-4">
        {canContinueSession && (
          <button
            onClick={handleContinueSession}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <Clock className="h-5 w-5 mr-2" />
            Continue Session
          </button>
        )}

        {canReopenSession && session.status !== 'in-progress' && session.status !== 'reopened' && (
          <button
            onClick={() => onReopenSession(session._id)}
            className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 flex items-center"
          >
            <AlertCircle className="h-5 w-5 mr-2" />
            Reopen Session
          </button>
        )}
      </div>
    </div>
  );
};

// Main ClientSessions Component
export const ClientSessions = () => {
  const navigate = useNavigate();
  const { get, put } = useApi();
  const { user } = useAuth();
  const [clientGroups, setClientGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedSessionRecord, setSelectedSessionRecord] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [recordLoading, setRecordLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
  });
  const [isReopening, setIsReopening] = useState(false);

  // View Switcher State
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    fetchSessionsByClient();
  }, []);

  const navigateDate = (direction) => {
    const amount = direction === 'next' ? 1 : -1;
    if (viewMode === 'day') setCurrentDate(prev => addDays(prev, amount));
    if (viewMode === 'week') setCurrentDate(prev => addWeeks(prev, amount));
    if (viewMode === 'month') setCurrentDate(prev => addMonths(prev, amount));
  };

  const getDateRangeLabel = () => {
    if (viewMode === 'day') return format(currentDate, 'MMMM d, yyyy');
    if (viewMode === 'week') {
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    }
    if (viewMode === 'month') return format(currentDate, 'MMMM yyyy');
  };

  const fetchSessionsByClient = async () => {
    try {
      setLoading(true);
      const response = await get('/sessions/clients');
      if (response.data?.status === 'success') {
        setClientGroups(response.data.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load client sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionDetails = async (sessionId) => {
    try {
      setSessionLoading(true);
      const response = await get(`/sessions/${sessionId}`);
      if (response.data?.status === 'success') {
        setSelectedSession(response.data.data);

        // Always fetch the session record to be safe
        // We'll determine later if it's initial or follow-up
        fetchSessionRecord(sessionId);
      }
    } catch (err) {
      setError(err.message || 'Failed to load session details');
    } finally {
      setSessionLoading(false);
    }
  };

  const fetchSessionRecord = async (sessionId) => {
    try {
      setRecordLoading(true);
      const response = await get(`/sessions/${sessionId}/record`);
      if (response.data?.status === 'success') {
        setSelectedSessionRecord(response.data.data.record);
      }
    } catch (err) {
      console.error('Failed to load counseling record:', err);
      // Don't set global error - just log it
    } finally {
      setRecordLoading(false);
    }
  };

  const handleExpandClient = (clientId) => {
    // If we're clicking on the same client, collapse it
    if (expandedClientId === clientId) {
      setExpandedClientId(null);
      setSelectedSessionId(null);
      setSelectedSession(null);
      setSelectedSessionRecord(null);
    } else {
      // Otherwise, expand the client and clear any selected session
      setExpandedClientId(clientId);
      setSelectedSessionId(null);
      setSelectedSession(null);
      setSelectedSessionRecord(null);
    }
  };

  const handleSelectSession = (sessionId, e) => {
    e.stopPropagation(); // Prevent client collapse/expand

    if (selectedSessionId === sessionId) {
      // Collapse if clicking the same session
      setSelectedSessionId(null);
      setSelectedSession(null);
      setSelectedSessionRecord(null);
    } else {
      // Otherwise, select the session and fetch details
      setSelectedSessionId(sessionId);
      fetchSessionDetails(sessionId);
    }
  };

  const handleContinueSession = (appointmentId) => {
    if (!appointmentId) {
      setError('Cannot continue session: Appointment ID is missing');
      return;
    }
    navigate(`/${user.role}/sessions/${appointmentId}`);
  };

  const handleReopenSession = async (sessionId) => {
    try {
      setIsReopening(true);
      setSelectedSessionId(sessionId);
      setError(null);

      // First, get the session details to extract the appointment ID
      const sessionResponse = await get(`/sessions/${sessionId}`);

      if (!sessionResponse.data?.status === 'success' || !sessionResponse.data?.data?.appointment?._id) {
        throw new Error('Failed to get appointment information for this session');
      }

      // Extract the appointment ID from the session data
      const appointmentId = sessionResponse.data.data.appointment._id;

      // Call the reopen endpoint
      const reopenResponse = await put(`/sessions/${sessionId}/reopen`);

      if (reopenResponse.data?.status === 'success') {
        // The session has been successfully reopened

        // Now navigate directly to the counseling session edit page using the appointment ID
        navigate(`/hod/sessions/${sessionId}/record`);
      } else {
        throw new Error('Failed to reopen session');
      }
    } catch (err) {
      console.error('Error reopening session:', err);
      setError(err.response?.data?.message || err.message || 'Failed to reopen session');
    } finally {
      setIsReopening(false);
    }
  };

  // Filter Logic
  const filteredClientGroups = clientGroups.map(group => {
    // 1. Filter sessions by Date
    const sessions = group.sessions.filter(session => {
      const sessionDate = new Date(session.sessionDate || session.createdAt);

      if (viewMode === 'day') {
        return isSameDay(sessionDate, currentDate);
      } else if (viewMode === 'week') {
        return isWithinInterval(sessionDate, {
          start: startOfWeek(currentDate),
          end: endOfWeek(currentDate)
        });
      } else if (viewMode === 'month') {
        return isSameMonth(sessionDate, currentDate);
      }
      return false;
    });

    // Return group with filtered sessions (we'll filter out empty groups next)
    return { ...group, sessions };
  }).filter(group => {
    // 2. Filter out groups with no sessions after date filtering
    if (group.sessions.length === 0) return false;

    if (!filters.search) return true;

    // 3. Search Filter
    const searchLower = filters.search.toLowerCase().trim();

    // Get the client name
    const clientName = group.clientName?.toLowerCase() || '';

    // Get student ID and staff ID directly from group.clientInfo
    const studentId = group.clientInfo?.studentId?.toString().toLowerCase() || '';
    const staffId = group.clientInfo?.staffId?.toString().toLowerCase() || '';

    // Search across all relevant fields
    const matchesSearch =
      clientName.includes(searchLower) ||
      studentId.includes(searchLower) ||
      staffId.includes(searchLower);

    return matchesSearch;
  });


  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Client Sessions</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400 dark:text-gray-400">
            View and manage your counseling sessions by client
          </p>
        </div>
        {/* View Switcher */}
        <div className="flex bg-gray-100 dark:bg-slate-800 rounded-lg p-1">
          {['day', 'week', 'month'].map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === mode
                  ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Date Navigation */}
      <div className="mb-6 flex items-center justify-between bg-white dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigateDate('prev')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-600 dark:text-gray-300"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-lg font-semibold text-gray-900 dark:text-white min-w-[200px] text-center">
            {getDateRangeLabel()}
          </span>
          <button
            onClick={() => navigateDate('next')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-600 dark:text-gray-300"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <button
            onClick={() => setCurrentDate(new Date())}
            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline px-2"
          >
            Today
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-slate-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search clients..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  search: e.target.value
                }))}
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Client Groups */}
      <div className="space-y-4">
        {filteredClientGroups.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">
              {clientGroups.length === 0
                ? 'No client sessions found'
                : 'No matching clients'
              }
            </h3>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              {clientGroups.length === 0
                ? 'No counseling sessions have been recorded yet.'
                : 'Try adjusting your search filters.'
              }
            </p>
          </div>
        ) : (
          filteredClientGroups.map(group => (
            <div
              key={group.clientId}
              className="bg-white dark:bg-slate-800 rounded-lg shadow hover:shadow-md transition-shadow duration-200 border border-gray-200 dark:border-gray-700"
            >
              <div
                className="p-6 cursor-pointer"
                onClick={() => handleExpandClient(group.clientId)}
              >
                {/* Client Header */}
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <User className="h-10 w-10 p-2 bg-blue-50 text-blue-600 rounded-full" />
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {group.clientName}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {group.totalSessions || group.sessions.length} session{(group.totalSessions || group.sessions.length) !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-6">
                    {/* Session Status Pills */}
                    <div className="hidden md:flex space-x-2">
                      {group.sessionStats?.inProgress > 0 && (
                        <div className="flex items-center space-x-1 bg-yellow-50 px-2 py-1 rounded-full">
                          <Clock className="h-3 w-3 text-yellow-500" />
                          <span className="text-xs font-medium text-yellow-700">{group.sessionStats.inProgress}</span>
                        </div>
                      )}
                      {group.sessionStats?.completed > 0 && (
                        <div className="flex items-center space-x-1 bg-green-50 px-2 py-1 rounded-full">
                          <CheckCircle className="h-3 w-3 text-green-500" />
                          <span className="text-xs font-medium text-green-700">{group.sessionStats.completed}</span>
                        </div>
                      )}
                      {group.sessionStats?.reopened > 0 && (
                        <div className="flex items-center space-x-1 bg-orange-50 px-2 py-1 rounded-full">
                          <AlertCircle className="h-3 w-3 text-orange-500" />
                          <span className="text-xs font-medium text-orange-700">{group.sessionStats.reopened}</span>
                        </div>
                      )}
                    </div>

                    <ChevronDown
                      className={`h-5 w-5 transform transition-transform duration-200
                        ${expandedClientId === group.clientId ? 'rotate-180' : ''}`}
                    />
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedClientId === group.clientId && (
                  <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {group.sessions.length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No sessions found for this client.</p>
                    ) : (
                      <div className="space-y-4">
                        {group.sessions.map(session => (
                          <div key={session._id} className="border-b pb-4 last:border-b-0 last:pb-0">
                            <div
                              className={`bg-gray-50 dark:bg-slate-900/50 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-slate-800 cursor-pointer border border-transparent dark:border-gray-700
                                ${selectedSessionId === session._id ? 'bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-200 dark:ring-blue-800' : ''}`}
                              onClick={(e) => handleSelectSession(session._id, e)}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="flex items-center space-x-2 mb-1">
                                    <h4 className="font-medium">
                                      Session {session.sessionNumber || '?'}
                                    </h4>
                                    <StatusBadge status={session.status} />
                                  </div>
                                  <div className="text-sm text-gray-500 flex items-center">
                                    <Calendar className="h-4 w-4 mr-1" />
                                    {new Date(session.sessionDate).toLocaleDateString()}
                                  </div>
                                </div>
                                {selectedSessionId === session._id ? (
                                  <ChevronDown className="h-5 w-5 text-blue-500" />
                                ) : (
                                  <ChevronDown className="h-5 w-5 text-gray-400" />
                                )}
                              </div>
                            </div>

                            {/* Session Details Section */}
                            {selectedSessionId === session._id && (
                              <div className="mt-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                                {sessionLoading ? (
                                  <div className="flex justify-center items-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                  </div>
                                ) : selectedSession ? (
                                  <SessionContent
                                    session={selectedSession}
                                    userRole={user.role}
                                    onContinueSession={handleContinueSession}
                                    onReopenSession={handleReopenSession}
                                    selectedSessionRecord={selectedSessionRecord}
                                    recordLoading={recordLoading}
                                  />
                                ) : (
                                  <div className="text-center py-4 text-gray-500">
                                    Failed to load session details. Please try again.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-lg shadow-lg z-50">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSessions;